<template>
  <ion-page>
    <main-header :back-href="reportsHref">
      <ion-toolbar>
        <ion-grid>
          <ion-row>
            <ion-col>
              <ion-grid class="ion-no-padding">
                <ion-row>
                  <ion-col class="ion-no-padding">
                    <ion-datetime
                      display-format="MM/DD/YYYY"
                      picker-format="MMM D, YYYY"
                      :value="startDateFilter"
                      @ionChange="handleFilteredStartDateAndUpdate"
                    ></ion-datetime>
                  </ion-col>
                </ion-row>
              </ion-grid>
            </ion-col>
            <ion-col>
              <ion-row>
                <ion-col class="ion-no-padding" size="10">
                  <ion-select
                    placeholder="- Choose Location -"
                    value
                    @ionChange="handleFilteredLocationAndUpdate"
                  >
                    <ion-select-option
                      v-for="location in locations"
                      v-bind:key="`location-${location.id}`"
                      :value="location.id"
                    >
                      {{ location.name }}
                    </ion-select-option>
                  </ion-select>
                </ion-col>
                <ion-col class="ion-no-padding ion-text-end" size="2">
                  <ion-button
                    @click="populateReconciliation()"
                    :disabled="!locationFilter"
                  >
                    Run
                  </ion-button>
                </ion-col>
              </ion-row>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-toolbar>
    </main-header>
    <ion-content padding>
      <loading-spinner v-if="loading"></loading-spinner>
      <div v-else>
        <div
          v-if="rows == null"
          class="background-color ion-color-light ion-padding ion-text-center"
        >
          Choose a location and confirm the selected date to run a
          reconciliation report.
        </div>
        <div
          v-else-if="rows.length == 0"
          class="background-color ion-color-warning ion-padding ion-text-center"
        >
          There are no records available.
        </div>
        <div v-else>
          <ion-grid class="order-table">
            <ion-row class="header">
              <ion-col size="2">
                Location
              </ion-col>
              <ion-col size="1">
                Date
              </ion-col>
              <ion-col size="1">
                Fulfillment
              </ion-col>
              <ion-col :size="userIsAdmin ? 1 : 3">
                Transaction ID
              </ion-col>
              <ion-col size="1" v-if="userIsAdmin">
                Customer ID
              </ion-col>
              <ion-col size="1" v-if="userIsAdmin">
                Customer
              </ion-col>
              <ion-col size="2">
                Item
              </ion-col>
              <ion-col size="1">
                Price
              </ion-col>
              <ion-col size="1">
                Quantity
              </ion-col>
              <ion-col size="1">
                Total
              </ion-col>
            </ion-row>
            <ion-row v-for="(row, idx) in rows" :key="`row-${idx}`">
              <ion-col size="2">
                {{ row.locationName }}
              </ion-col>
              <ion-col size="1">
                {{ row.created }}
              </ion-col>
              <ion-col size="1">
                {{
                  row.fulfillment === ORDER_FULFILLMENT_PICKUP
                    ? "Pick-up"
                    : "Delivery"
                }}
              </ion-col>
              <ion-col :size="userIsAdmin ? 1 : 3">
                <small>{{ row.id }}</small>
              </ion-col>
              <ion-col size="1" v-if="userIsAdmin">
                <small>{{ row.userId }}</small>
              </ion-col>
              <ion-col size="1" v-if="userIsAdmin">
                {{ row.displayName }}
              </ion-col>
              <ion-col size="2">
                {{ row.item }}
                {{ row.option ? "- " + row.option : "" }}
              </ion-col>
              <ion-col size="1"> ${{ row.price }} </ion-col>
              <ion-col size="1">
                {{ row.quantity }}
              </ion-col>
              <ion-col size="1"> ${{ row.total }} </ion-col>
            </ion-row>
          </ion-grid>
          <hr />
          <div class="ion-text-end">
            <ion-button @click="download()">
              Download
              <ion-icon slot="end" name="cloud-download"></ion-icon>
            </ion-button>
          </div>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
import { mapGetters } from "vuex";
import Papa from "papaparse";
import LocationModelService from "@/services/LocationModel";
import errorAlert from "@/ErrorAlert";
import { centsToDollars } from "@/util";
import { ORDER_FULFILLMENT_PICKUP } from "@/constants/order";

export default {
  title: "Reconciliation Report",
  props: {
    startDate: {
      required: false,
      type: String
    }
  },

  data: function() {
    return {
      ORDER_FULFILLMENT_PICKUP,
      loading: true,
      rows: null,
      locations: [],
      locationFilter: "",
      startDateFilter: ""
    };
  },

  async created() {
    if (!this.userIsAdmin && !this.userIsVendor) {
      return this.$router.push("/").catch(e => e);
    }

    this.setInitialDates();

    await this.populateLocations();

    this.loading = false;
  },

  methods: {
    handleFilteredStartDateAndUpdate(event) {
      this.startDateFilter = event.detail.value;
    },

    handleFilteredLocationAndUpdate(event) {
      this.locationFilter = event.detail.value;
    },

    setInitialDates() {
      let now;

      if (this.startDate && /^\d{4}-\d{2}-\d{2}$/.test(this.startDate)) {
        let parts = this.startDate.split("-");
        now = new Date(
          parseInt(parts[0]),
          parseInt(parts[1]) - 1,
          parseInt(parts[2])
        );
      }

      now = now || (d => new Date(d.setDate(d.getDate() - 1)))(new Date());

      this.startDateFilter = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate()
      ).toDateString();
    },

    async populateLocations() {
      let q = LocationModelService.currentUserLocations();
      return q.then(locations => {
        this.locations = locations.data;
      });
    },

    populateReconciliation() {
      this.loading = true;
      this.rows = [];

      let reconciliationReport = this.$functions().httpsCallable(
        "reconciliationReport"
      );

      reconciliationReport({
        locationId: this.locationFilter,
        startDate: new Date(this.startDateFilter).toISOString().split("T")[0],
        timezoneOffset: new Date().getTimezoneOffset()
      })
        .then(results => {
          this.rows = results.data.map(data => {
            return {
              ...data,
              price: centsToDollars(data.price),
              total: centsToDollars(data.total)
            };
          });
        })
        .catch(e => {
          errorAlert(e.message);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    download() {
      let csv = Papa.unparse(
        this.rows.map(row => {
          let object = {
            Location: row.locationName,
            Date: row.created,
            Fulfillment:
              row.fulfillment === ORDER_FULFILLMENT_PICKUP
                ? "Pick-up"
                : "Delivery",
            "Transaction ID": row.id,
            Item: row.item,
            Price: row.price,
            Quantity: row.quantity,
            Total: row.total
          };

          if (row.option) {
            object.Item += " - " + row.option;
          }

          if (this.userIsAdmin) {
            object["Customer ID"] = row.userId;
            object["Customer Name"] = row.displayName;
          }

          return object;
        })
      );

      let blob = new Blob([csv]);
      let a = window.document.createElement("a");
      a.href = window.URL.createObjectURL(blob, { type: "text/csv" });
      a.download = "reconciliation-report.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  },

  computed: {
    reportsHref() {
      return this.$router.resolve({
        name: "Web: Reports"
      }).href;
    },

    ...mapGetters("auth", ["userIsAdmin", "userIsVendor"])
  }
};
</script>
